import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as moment from 'moment';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { JobTitle } from '../job-titles.types';
import { HttpErrorResponse } from '@angular/common/http';
import { JobTitlesService } from '../job-titles.service';
import { AlertDialogComponent } from '../../dialogs';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

@Component({
	selector: 'app-job-titles-details',
	templateUrl: './job-titles-details.component.html',
	styleUrls: ['./job-titles-details.component.scss']
})
export class JobTitlesDetailsComponent implements OnInit {
	constructor(
		private route: ActivatedRoute,
		private formBuilder: FormBuilder,
		private router: Router,
		private jobTitlesService: JobTitlesService,
		public dialog: MatDialog
	) {
		this.id = this.route.snapshot.paramMap.get('id');
		this.form = this.formBuilder.group({
			name: new FormControl(this.jobTitle.name, [Validators.required]),
			abbreviation: new FormControl(this.jobTitle.abbreviation, [Validators.required]),
			effectiveStart: new FormControl(moment.utc())
		});
		this.dialogConfig = new MatDialogConfig();
	}
	public id: string = '';
	public dates: string[] = [];
	public currentDate: string;
	public form: FormGroup;
	public cancel: boolean = false;
	public saveNew: boolean = false;
	public saveChanges: boolean = false;
	public isDelete: boolean = true;
	public isArchived: boolean = false;
	public jobTitle: JobTitle = new JobTitle();
	public alertDialogRef: MatDialogRef<AlertDialogComponent>;
	public dialogConfig: MatDialogConfig;

	ngOnInit(): void {
		const minDateValue: moment.Moment = moment
			.utc()
			.startOf('month')
			.subtract(1, 'M');
		const maxDateValue: moment.Moment = moment
			.utc()
			.startOf('month')
			.add(12, 'M');
		this.getDates(minDateValue, maxDateValue);

		// id will be 0 if creating a new job title
		if (this.id === '0') {
			this.isDelete = false;
		} else {
			if (this.route.snapshot.url.toString().includes('archived')) {
				this.getArchivedJobTitle(this.id);
			} else {
				this.getJobTitle(this.id);
			}
		}
	}

	public getDates(startDate: moment.Moment, endDate: moment.Moment): string[] {
		const now: moment.Moment = startDate.clone();

		while (now.isSameOrBefore(endDate)) {
			this.dates.push(now.format('YYYY-MM-DDTHH:mm:ss'));
			now.add(1, 'month');
		}

		const currentDate: string = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
		this.currentDate = this.dates.find(d => new Date(d).toISOString() === currentDate);
		this.form.get('effectiveStart').setValue(this.currentDate);
		return this.dates;
	}

	public chosenMonthHandler(normalizedMonth: moment.Moment): void {
		this.form.get('effectiveStart').patchValue(normalizedMonth);
	}

	public navigate(): void {
		this.router.navigate(['/pilotpay/job-titles']);
	}

	public toggleButtons(): void {
		this.cancel = true;
		if (this.id === '0') {
			this.saveNew = true;
		} else {
			this.saveChanges = true;
		}
	}

	public createNewJobTitle(): void {
		this.jobTitle.effectiveStart = this.form.value.effectiveStart;
		this.jobTitle.name = this.form.value.name;
		this.jobTitle.abbreviation = this.form.value.abbreviation;

		this.jobTitlesService.addJobTitle(this.jobTitle).subscribe(
			() => {
				this.navigate();
			},
			(error: HttpErrorResponse) => {}
		);
	}

	public deleteTitle(): void {
		this.dialogConfig.disableClose = false;
		this.dialogConfig.autoFocus = true;
		this.dialogConfig.data = {
			modalType: 'delete',
			name: this.jobTitle.name,
			modifiedDate: this.form.value.effectiveStart
		};

		if (this.dialog) {
			this.alertDialogRef = this.dialog.open(AlertDialogComponent, this.dialogConfig);
		}
		if (this.alertDialogRef) {
			this.alertDialogRef.beforeClosed().subscribe(() => {});
			this.alertDialogRef.afterClosed().subscribe((isDelete: boolean) => {
				if (isDelete) {
					const endDate: any = this.form.value.effectiveStart;
					this.jobTitlesService.deleteJobTitle(this.id, endDate).subscribe(
						() => {
							this.navigate();
						},
						(error: HttpErrorResponse) => {
							console.error(error);
						}
					);
				}
			});
		}
	}

	public getJobTitle(id: string): void {
		this.jobTitlesService.getJobTitleById(id).subscribe(
			result => {
				this.jobTitle = result;
				this.form.patchValue({ name: this.jobTitle.name });
				this.form.patchValue({ abbreviation: this.jobTitle.abbreviation });
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	public getArchivedJobTitle(id: string): void {
		this.jobTitlesService.getArchivedJobTitleById(id).subscribe(
			result => {
				this.jobTitle = result;
				this.form.patchValue({ name: this.jobTitle.name });
				this.form.patchValue({ abbreviation: this.jobTitle.abbreviation });
				this.isArchived = true;
				this.form.controls['name'].disable();
				this.form.controls['abbreviation'].disable();
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	public saveChangesClicked(): void {
		this.jobTitle.effectiveStart = this.form.value.effectiveStart;
		this.jobTitle.name = this.form.value.name;
		this.jobTitle.abbreviation = this.form.value.abbreviation;

		this.jobTitlesService.updateJobTitle(this.jobTitle).subscribe(
			() => {
				this.navigate();
			},
			(error: HttpErrorResponse) => {}
		);
	}
}
